import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "../signaturePad/index.css";

export default function SignaturePad({
  isOpen = false,
  onClose,
  onSave,
  onClear,
  title = "Signature",
  existingSign = "",
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);
  const [mounted, setMounted] = useState(false);

  /* ── mount / unmount ── */
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setIsEmpty(true);
    }
  }, [isOpen]);

  /* ── init canvas when modal opens ── */
  useEffect(() => {
    if (!mounted || !isOpen) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    /* hi-DPI fix */
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (existingSign) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setIsEmpty(false);
      };
      img.src = existingSign;
    }
  }, [mounted, isOpen, existingSign]);

  /* ── pointer helpers ── */
  const getXY = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    drawing.current = true;
    lastPos.current = getXY(e, canvas);
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getXY(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
  }, []);

  const endDraw = useCallback(() => {
    drawing.current = false;
  }, []);

  /* ── actions ── */
  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width * dpr, rect.height * dpr);
    setIsEmpty(true);
    onClear?.();
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || isEmpty) return;
    const dataUrl = canvas.toDataURL("image/png");
    onSave?.(dataUrl);
    handleClose();
  };

  const handleClose = () => {
    setMounted(false);
    onClose?.();
  };

  if (!isOpen && !mounted) return null;

  /* ── render into document.body via Portal ──
     This escapes any parent with transform / overflow / will-change
     that would break position:fixed centering                        */
  return createPortal(
    <div
      className={`sp-overlay${isOpen ? " sp-overlay--in" : " sp-overlay--out"}`}
      onAnimationEnd={() => {
        if (!isOpen) setMounted(false);
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className={`sp-modal${isOpen ? " sp-modal--in" : " sp-modal--out"}`}
      >
        {/* header */}
        <div className="sp-header">
          <div className="sp-header-left">
            <span className="sp-pen-icon">✍</span>
            <div>
              <div className="sp-header-title">{title}</div>
              <div className="sp-header-sub">Draw your signature below</div>
            </div>
          </div>
          <button
            className="sp-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* canvas area */}
        <div className="sp-canvas-wrapper">
          <canvas
            ref={canvasRef}
            className="sp-canvas"
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {isEmpty && (
            <div className="sp-placeholder">
              <span className="sp-placeholder-icon">✍</span>
              <span>Sign here</span>
            </div>
          )}
          <div className="sp-baseline" />
        </div>

        {/* footer */}
        <div className="sp-footer">
          <button className="sp-btn sp-btn--ghost" onClick={handleClear}>
            <span>↺</span> Clear
          </button>
          <div className="sp-footer-right">
            <button className="sp-btn sp-btn--cancel" onClick={handleClose}>
              Cancel
            </button>
            <button
              className={`sp-btn sp-btn--save${isEmpty ? " sp-btn--disabled" : ""}`}
              onClick={handleSave}
              disabled={isEmpty}
            >
              <span>✓</span> Save Signature
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
} 